import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { useDispatch } from 'react-redux';

import TextArea from 'components/TextArea';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import Card from 'components/Card';
import Select from 'components/Select';
import DatePicker from 'components/DatePicker';
import SelectCreatable from 'components/SelectCreatable';

import { Container, Form, FormGroups } from './styles';

import api from 'services/api';
import AppError from 'utils/AppError';
import getValidationErrors from 'services/getValidationErros';
import { show } from 'store/modules/dialog/actions';

// configuração do validator
const schema = Yup.object().shape({
  programa: Yup.string().required('Campo Obrigatório'),
  emissora: Yup.string().required('Campo Obrigatório'),
  dataentrega: Yup.string()
    .required('Campo Obrigatório')
    .nullable(),
  informacoes: Yup.string().required('Campo Obrigatório'),
});
export default function SugestaoPromoStore() {
  // react-router-dom
  const navigate = useNavigate();
  /**
   * USE STATE
   */
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [programas, setProgramas] = useState([]);
  const [emissoras, setEmissoras] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  /**
   * REDUX
   */
  const dispatch = useDispatch();
  /**
   * USE REF
   */
  const formRef = useRef<FormHandles>(null);

  /**
   * FUNÇÕES
   */
  useEffect(() => {
    async function loadOptions() {
      setLoadingOptions(true);
      try {
        const [responseProgramas, responseEmissoras] = await Promise.all([
          api.get('/programas'),
          api.get('/emissoras'),
        ]);

        if (responseProgramas?.data && responseEmissoras?.data) {
          setProgramas(
            responseProgramas.data.map(p => ({
              value: p.nome,
              label: p.nome,
            })),
          );
          const emissorasNormalized = responseEmissoras.data.map(e => ({
            value: e.nome,
            label: e.nome,
          }));
          emissorasNormalized.unshift({ value: 'REDE', label: 'REDE' });
          setEmissoras(emissorasNormalized);
        } else toast.error('Falha de comunicação com o sistema');
        setLoadingOptions(false);
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
    }
    loadOptions();
  }, []);

  const handleSuccessMessage = useCallback(
    ({ contentHTML, redirectTo }) => {
      const option = {
        type: 'success',
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async sugestaopromo => {
      try {
        if (formRef.current) {
          setLoadingSubmit(true);
          formRef.current.setErrors({});
          await schema.validate(sugestaopromo, { abortEarly: false });
          const response = await api.post('/sugestaopromo', sugestaopromo);
          if (response && response.data)
            handleSuccessMessage({
              contentHTML: `<h3>Sugestão de Promo salva!</h3> <p>Você será redirecionado para a lista de sugestões em 3 segundos</p>`,
              redirectTo: `/sugestaopromo`,
            });
          else toast.error('Falha ao salvar a sugestão de Promo');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingSubmit(false);
    },
    [handleSuccessMessage],
  );

  return (
    <Container>
      <Card>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <div>
            <header>
              <h4>CRIAR NOVA SUGESTÃO DE PROMO</h4>
            </header>
            <div>
              <section>
                <h5>Para qual Programa? *</h5>
              </section>
              <SelectCreatable
                max={100}
                isClearable
                menuPlacement="auto"
                isMulti={false}
                errorField=""
                name="programa"
                placeholder="Programa"
                options={programas}
                searchable
                createOptionPosition="first"
                fill={false}
              />
              <FormGroups $columns="1fr 1fr">
                <div>
                  <section>
                    <h5>Qual é a praça?: *</h5>
                  </section>
                  <Select
                    id="emissora"
                    errorField=""
                    name="emissora"
                    placeholder="Emissora"
                    options={emissoras}
                    disabled={loadingOptions || loadingSubmit}
                    isSearchable
                    fill={false}
                  />
                </div>
                <div>
                  <section>
                    <h5>
                      Indique a data ideal para receber esse material
                      (confirmaremos a viabilidade dessa entrega, tá?) *
                    </h5>
                  </section>
                  <DatePicker
                    placeholderText="Escolha uma data"
                    minDate={new Date()}
                    name="dataentrega"
                    disabled={loadingSubmit}
                  />
                </div>
              </FormGroups>
              <section>
                <h5>
                  Fala pra gente: do que você precisa? Qual a ideia? Como a
                  oportunidade surgiu? *
                </h5>
              </section>
              <TextArea
                name="informacoes"
                height="200px"
                icon=""
                errorField=""
                placeholder="Quanto mais informações, melhor para captarmos a essência do que você precisa *"
              />
              <section>
                <h5>
                  Existe algum material gravado (da RPC ou de programa da Rede)
                  que pode nos ajudar?
                </h5>
              </section>
              <TextArea
                name="materialgravado"
                height="200px"
                icon=""
                errorField=""
                placeholder="Cole o link do Globoplay ou caminho da pasta"
              />
            </div>
          </div>
          <div className="ButtonGroup">
            <Button
              color="primary"
              onClick={e => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              <FiArrowLeft />
              VOLTAR
            </Button>
            <Button className="submitButton" type="submit" color="primary">
              <FiSave />
              {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
}
